import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {useLocation} from 'react-router'
import {checkIsActive, KTSVG} from '../../../helpers'
import {useLayout} from '../../core'

type Props = {
  to: string
  title: string
  icon?: string
  fontIcon?: string
  hasBullet?: boolean
  activePath?: string
}

const ASIDE_MENU_ACCORDION_OPEN_EVENT = 'aside-menu-accordion-open'

const AsideMenuItemWithSub: React.FC<Props> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet,
  activePath,
}) => {
  const {pathname} = useLocation()
  const isActive = checkIsActive(pathname, activePath ?? to)
  const [isOpen, setIsOpen] = useState(isActive)
  const [submenuPosition, setSubmenuPosition] = useState<{left: number; top: number} | null>(null)
  const menuItemRef = useRef<HTMLDivElement>(null)
  const submenuRef = useRef<HTMLDivElement>(null)
  const {config} = useLayout()
  const {aside} = config
  const isMasterLanguage = pathname.includes('/settings/master-language') && title === 'Settings'

  const openAccordion = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent(ASIDE_MENU_ACCORDION_OPEN_EVENT, {
        detail: menuItemRef.current,
      })
    )
    setIsOpen(true)
  }, [])

  const toggleAccordion = useCallback(() => {
    if (isOpen) {
      setIsOpen(false)
      return
    }

    openAccordion()
  }, [isOpen, openAccordion])

  useEffect(() => {
    const handleAccordionOpen = (event: Event) => {
      const openedMenuItem = (event as CustomEvent<HTMLDivElement>).detail

      if (openedMenuItem !== menuItemRef.current) {
        setIsOpen(false)
      }
    }

    window.addEventListener(ASIDE_MENU_ACCORDION_OPEN_EVENT, handleAccordionOpen)
    return () => window.removeEventListener(ASIDE_MENU_ACCORDION_OPEN_EVENT, handleAccordionOpen)
  }, [])

  const updateSubmenuPosition = useCallback(() => {
    if (document.body.getAttribute('data-kt-aside-minimize') !== 'on') {
      setSubmenuPosition(null)
      return
    }

    const menuItem = menuItemRef.current
    if (!menuItem) {
      return
    }

    const menuItemRect = menuItem.getBoundingClientRect()
    const submenuHeight = submenuRef.current?.getBoundingClientRect().height ?? 0
    const maxTop = window.innerHeight - submenuHeight - 8
    const top = submenuHeight > 0 ? Math.max(8, Math.min(menuItemRect.top, maxTop)) : menuItemRect.top

    setSubmenuPosition({left: menuItemRect.right, top})
  }, [])

  useLayoutEffect(() => {
    if (!submenuPosition || !submenuRef.current || !menuItemRef.current) {
      return
    }

    const menuItemRect = menuItemRef.current.getBoundingClientRect()
    const submenuHeight = submenuRef.current.getBoundingClientRect().height
    const maxTop = window.innerHeight - submenuHeight - 8
    const top = Math.max(8, Math.min(menuItemRect.top, maxTop))

    if (top !== submenuPosition.top || menuItemRect.right !== submenuPosition.left) {
      setSubmenuPosition({left: menuItemRect.right, top})
    }
  }, [submenuPosition])

  useEffect(() => {
    if (!submenuPosition) {
      return
    }

    window.addEventListener('resize', updateSubmenuPosition)
    return () => window.removeEventListener('resize', updateSubmenuPosition)
  }, [submenuPosition, updateSubmenuPosition])

  useEffect(() => {
    // Keep the active section expanded after route changes, while still allowing
    // the user to collapse it manually on the current page.
    if (isActive) {
      openAccordion()
    }
  }, [isActive, openAccordion])

  return (
    <div
      ref={menuItemRef}
      onMouseEnter={updateSubmenuPosition}
      onMouseLeave={() => setSubmenuPosition(null)}
      className={clsx(
        'menu-item',
        {here: isActive, show: isOpen},
        'menu-accordion has-children',
        isMasterLanguage ? 'mb-8' : ''
      )}
    >
      <button
        type='button'
        className='menu-link border-0 text-start'
        onClick={toggleAccordion}
        aria-expanded={isOpen}
        data-menu-title={title}
      >
        {hasBullet && (
          <span className='menu-bullet'>
            <span className='bullet bullet-dot'></span>
          </span>
        )}
        {icon && aside.menuIcon === 'svg' && (
          <span className='menu-icon'>
            <KTSVG path={icon} className='svg-icon-2' />
          </span>
        )}
        {fontIcon && aside.menuIcon === 'font' && <i className={clsx('bi fs-3', fontIcon)}></i>}
        <span className='menu-title'>{title}</span>
        <span className='menu-arrow'></span>
      </button>
      <div
        ref={submenuRef}
        className={clsx('menu-sub menu-sub-accordion', {'menu-active-bg': isActive})}
        style={submenuPosition ? {position: 'fixed', left: submenuPosition.left, top: submenuPosition.top} : undefined}
      >
        {children}
      </div>
    </div>
  )
}

export {AsideMenuItemWithSub}
